import type { FC } from "react";

import type { IEntryPointComponent } from "@/src/common/PageTransition";
import { languageSelector } from "@/src/contexts";
import { FlyersTabResources } from "@/src/flyers/FlyersTabResources.Resources";
import { FlyersTabResourcesEn } from "@/src/flyers/FlyersTabResources.Resources.En";
import { useQueryPageCustom } from "@/src/query-client-factory";
import PageLayout from "@/src/shared/page-layout";
import Container from "@Components/Container";
import { FlyersPage } from "@Routes/FlyersLinks";
import type { QueryXsMax } from "@stylex/tokens.stylex";
import * as stylex from "@stylexjs/stylex";

import { LocationInfo } from "../location/LocationInfo";
import type { IFlyersTabBrands } from "./FlyersTabBrands";
import FlyersTabBrands from "./FlyersTabBrands";
import type { IMostPopularFlyersSlider } from "./MostPopularFlyersSlider";
import MostPopularFlyersSlider from "./MostPopularFlyersSlider";

export interface IFlyersTab extends IEntryPointComponent {
    mostPopularFlyersSlider: IMostPopularFlyersSlider;
    brands: IFlyersTabBrands;
}

export const FlyersTab: FC<IFlyersTab> = () => {
    const { data } = useQueryPageCustom<IFlyersTab>();

    const resources = languageSelector(FlyersTabResources, FlyersTabResourcesEn);

    const props = data.component;

    return (
        <PageLayout
            breadcrumbs={[{ labelKey: "flyersTab" }]}
            layoutAdUnitPath="gazetki"
            slugTarget={FlyersPage}
        >
            <Container>
                <div {...stylex.props(styles.mostPopularFlyersSlider)}>
                    <MostPopularFlyersSlider
                        {...props.mostPopularFlyersSlider}
                        resources={resources.mostPopularFlyers}
                    />
                </div>
                <LocationInfo
                    title={resources.locationTile}
                    className={stylex.props(styles.locationInfo).className}
                />
            </Container>

            <Container>
                <FlyersTabBrands {...props.brands} itemsToShow={20} resources={resources.brands} />
            </Container>
        </PageLayout>
    );
};

const xsMax: QueryXsMax = "@media (max-width: 759px)";

const styles = stylex.create({
    mostPopularFlyersSlider: {
        margin: {
            default: null,
            [xsMax]: 0,
        },
        padding: {
            default: null,
            [xsMax]: 0,
        },
        visibility: "visible",
        marginBottom: 0,
        marginTop: 0,
    },

    locationInfo: {
        strokeWidth: 2,
        paddingBottom: 20,
        paddingLeft: 0,
        paddingRight: 0,
        paddingTop: 20,
    },
});
